import React from 'react';
import { useNavigate, Route, Routes, Navigate } from "react-router-dom";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Layout from './Layout';
import Session from './shared/Session';
import { ProtectedRoute } from './shared/Utilities';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import LoginDialog from './shared/LoginDialog';

import MiniProjects from './views/MiniProjects';
import Secret from './views/Secret';
import ReportBuilder from './views/ReportBuilder';
import SchemaForm from './views/SchemaForm';
import ApiBrowser from './views/ApiBrowser';
import Tasks from './views/Tasks';
import NotFound from './views/NotFound';
import Home from './views/Home';
import Secrets from './views/Secrets';
import AI from './views/AI';
import Data from './views/Data';
import CreateApp from './views/CreateApp';
import Code from './views/Code';
import Applications from './views/Applications';
import Chat from './views/Chat';
import Viewer from './views/Viewer';

import { useGoogleLogin } from '@react-oauth/google';

const defaultTheme = createTheme();

type InnerAppState = {
    snackbarOpen: boolean,
    message: string,
    snackbarSeverity: string,
    backdropOpen: boolean
};

class InnerApp extends React.Component<any, InnerAppState> {
    constructor(props: any) {
        super(props);
        this.state = {
            snackbarOpen: false,
            message: "Welcome!",
            snackbarSeverity: "info",
            backdropOpen: false
        };
        Session.app = this;
        Session.verify_token(false, true);
    }

    handleClose = () => {
        this.setState({
            snackbarOpen: false
        });
    };

    openBackdrop = () => {
        this.setState({ backdropOpen: true });
    };

    closeBackdrop = () => {
        this.setState({ backdropOpen: false });
    };

    render() {
        return (
            <>
                <div id="background1"></div>
                <div id="background2"></div>

                <LoginDialog />

                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    open={this.state.snackbarOpen}
                    onClose={this.handleClose}
                    autoHideDuration={6000}
                    key={"snackbar-center"}
                >
                    <MuiAlert
                        elevation={6}
                        variant="filled"
                        onClose={this.handleClose}
                        severity={this.state.snackbarSeverity as 'error' | 'info' | 'success' | 'warning'}
                        sx={{ width: '100%' }}
                    >
                        {this.state.message}
                    </MuiAlert>
                </Snackbar>

                <Backdrop open={this.state.backdropOpen} sx={{ color: '#fff', zIndex: 10000 }}>
                    <CircularProgress />
                </Backdrop>

                <ThemeProvider theme={defaultTheme}>
                    <Layout>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route element={<ProtectedRoute />}>
                                <Route path="/report/new" element={<ReportBuilder />} />
                                <Route path="/data" element={<Data />} />
                                <Route path="/applications" element={<Applications />} />
                                <Route path="/tasks" element={<Tasks />} />
                                <Route path="/api" element={<ApiBrowser />} />
                                <Route path="/ai/mini_projects" element={<MiniProjects />} />
                                <Route path="/ai" element={<AI />} />
                                <Route path="/secrets" element={<Secrets />} />
                                <Route path="/code" element={<Code />} />
                                <Route path="/applications/new" element={<CreateApp />} />
                                <Route path="/:schema_name/new" element={<SchemaForm />} />
                                <Route path="/:schema_name/edit/:id" element={<SchemaForm />} />
                                <Route path="/:schema_name/:id" element={<Viewer />} />
                                <Route path="/:parent_schema_name/:schema_name/new" element={<SchemaForm />} />
                                <Route path="/:parent_schema_name/:schema_name/edit/:id" element={<SchemaForm />} />
                                <Route path="/:parent_schema_name/:schema_name/:id" element={<Viewer />} />
                            </Route>
                            {/* Public Routes */}
                            <Route path="/chat" element={<Chat />} />
                            <Route path="/secret/:secret_key/:key" element={<Secret />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Layout>
                </ThemeProvider>
            </>
        );
    }
};

export default function App(props: any) {
    Session.navigate = useNavigate();
    Session.googleLoginHook = useGoogleLogin({
        onSuccess: (response) => {
            Session.googleLogin(
                response.access_token,
                (result: any) => {
                    localStorage.setItem(Session.TOKEN_KEY, result);
                    Session.token = result;
                    console.log("Login Dialog: Verify Token");
                    Session.verify_token(false, true);
                },
                (result: any) => {
                    (document.activeElement as HTMLElement).blur();
                    (document.getElementById("login-form") as HTMLFormElement).reset();
                    Session.app.setState({
                        snackbarOpen: true,
                        snackbarSeverity: "error",
                        message: result
                    });
                    Session.loginDialog.setState({
                        loading: false
                    });
                }
            );
        },
        onError: (error) => {
            console.log(error);
            console.log("Google login canceled.");
        }
    })

    if (Session.verified && Session.redirectPath !== "") {
        Session.redirectPath = "";
        console.log("App navigation!");
        return <Navigate to={Session.redirectPath} />;
    }

    return <InnerApp {...props} />;
}
